import { Controller } from 'stimulus'
import consumer from 'channels/consumer'
import { applyStickyTransforms } from '@helpers'

export default class extends Controller {
  static targets = ['container', 'pagination']

  connect() {
    const {
      resourceType,
      resourceId,
      resourceScope,
      refreshTargetId,
      spinnerId
    } = this.element.dataset
    this.spinnerId = spinnerId
    this.refreshTargetId = refreshTargetId
    this.subscriptionParams = {
      channel: 'ResourceableChannel',
      resource_type: resourceType,
      resource_id: resourceId
    }

    if (resourceScope) {
      this.subscriptionParams.resource_scope = resourceScope
    }

    console.log('[ResourceableChannel] Controller connected', {
      subscriptionParams: this.subscriptionParams,
      hasContainer: this.hasContainerTarget
    })

    this.subscription = consumer.subscriptions.create(
      this.subscriptionParams,
      { received: this.handleReceived.bind(this) }
    )

    this.startPingMonitor()
    this.initializeLazyLoad()
    this._initialPaginationHTML = this.hasPaginationTarget
      ? this.paginationTarget.outerHTML
      : null
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
    if (this.pingInterval) clearInterval(this.pingInterval)
    if (this.intersectionObserver) this.intersectionObserver.disconnect()
    if (this.deferredReplaces) this.deferredReplaces.clear()
  }

  reloadAll(data) {
    const { html, message, skip_message, undo } = data
    console.log('[ResourceableChannel] reloadAll called', { hasHtml: !!html, hasContainer: this.hasContainerTarget, refreshTargetId: this.refreshTargetId })

    if (this.element.parentElement && this.element.parentElement.closest('[data-controller~="resourceable-channel"]')) {
      console.log('[ResourceableChannel] Ancestor channel will handle reloadAll, skipping')
      return
    }

    if (!html) return

    const target = this.refreshTargetId ? document.getElementById(this.refreshTargetId) : (this.hasContainerTarget ? this.containerTarget : null)
    if (!target) {
      console.log('[ResourceableChannel] No target for reloadAll, skipping')
      return
    }

    target.innerHTML = html
    // Let the rest of the page react (the task's Results Summary re-fetches).
    this.element.dispatchEvent(new CustomEvent('resourceable:reloaded', { bubbles: true }))

    requestAnimationFrame(() => {
      if (this._initialPaginationHTML && this.hasPaginationTarget) {
        this.paginationTarget.outerHTML = this._initialPaginationHTML
      }

      const pagination = this.element.querySelector('[data-target="resourceable-channel.pagination"]')
      if (pagination) pagination.classList.remove('hidden')

      this.applySticky()
      this.teardownLazyLoad()
      this.initializeLazyLoad()

      if (pagination && this.isPaginationVisibleInRoot()) this.loadMore()

      if (skip_message) return

      if (undo && undo.url) {
        this.showUndoToast(message || 'Item removed.', undo)
      } else {
        this.showToast('success', message || 'Content reloaded.')
      }
    })
  }

  showUndoToast(message, undo) {
    const linkId = `undo-link-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
    const safeMessage = this.escapeHtml(message)
    const safeLabel = this.escapeHtml(undo.label || 'Undo')
    const html = `${safeMessage} <a href="#" id="${linkId}" style="color:#fff;text-decoration:underline;font-weight:bold;margin-left:8px;">${safeLabel}</a>`

    const toast = window.toastr.info(html, null, {
      timeOut: 8000,
      extendedTimeOut: 4000,
      closeButton: true,
      tapToDismiss: false,
      preventDuplicates: false,
      preventOpenDuplicates: false
    })

    const link = document.getElementById(linkId)
    if (!link) return

    link.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      link.style.pointerEvents = 'none'

      Rails.ajax({
        type: 'POST',
        url: undo.url,
        dataType: 'script',
        error: () => window.toastr.error('Undo failed')
      })

      const toastEl = link.closest('.toast')
      if (toastEl) toastEl.remove()
    })
  }

  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str == null ? '' : String(str)
    return div.innerHTML
  }

  teardownLazyLoad() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
  }

  initializeLazyLoad() {
    if (!this.hasContainerTarget || !this.hasPaginationTarget) return

    this.teardownLazyLoad()

    const root = this.getScrollRoot()
    this.intersectionObserver = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && this.loadMore()),
      { root, rootMargin: '200px' }
    )

    this.intersectionObserver.observe(this.paginationTarget)
  }

  loadMore() {
    if (this.isLoading || !this.hasPaginationTarget) return

    const nextLink = this.paginationTarget.querySelector("a[rel='next']")
    if (!nextLink) {
      this.paginationTarget.classList.add('hidden')
      return
    }

    this.isLoading = true
    this.showSpinner()
    this.toggleAddButton(true)

    Rails.ajax({
      type: 'GET',
      url: nextLink.href,
      dataType: 'json',
      success: (data) => {
        this.containerTarget.insertAdjacentHTML('beforeend', data.entries)
        this.paginationTarget.innerHTML = data.pagination
        this._lastLoadFailed = false
      },
      error: () => {
        this._lastLoadFailed = true
      },
      complete: () => {
        this.isLoading = false
        this.hideSpinner()
        this.toggleAddButton(false)
        if (!this._lastLoadFailed && this.isPaginationVisibleInRoot()) this.loadMore()
        this.applySticky()
      }
    })
  }

  toggleAddButton(disabled) {
    const btn = document.getElementById('add-item')
    if (!btn) return
    btn.disabled = disabled
    btn.classList.toggle('disabled', disabled)
  }

  add(data) {
    const { html, message, item_id: itemId } = data
    console.log('[ResourceableChannel] add called', { itemId, hasHtml: !!html, hasContainer: this.hasContainerTarget })

    if (!html || !this.hasContainerTarget) return

    if (this.isLoading) return

    if (this.containerTarget.querySelector(`[data-item-id="${itemId}"]`)) return

    this.showSpinner()
    this.containerTarget.insertAdjacentHTML('beforeend', html)
    const el = this.containerTarget.querySelector(`[data-item-id="${itemId}"]`)
    this.handleEffect({ element: el })
    this.showToast('success', message || 'Item added.')
  }

  replace(data) {
    const { html, message, item_id: itemId, scroll = false, close_modal = false } = data

    console.log('[ResourceableChannel] replace called', { itemId, hasHtml: !!html, message, hasContainer: this.hasContainerTarget })

    if (!this.hasContainerTarget) {
      console.log('[ResourceableChannel] No container target, skipping replace')
      return
    }

    if (!html) {
      console.log('[ResourceableChannel] No HTML provided, skipping replace')
      return
    }

    const item = this.containerTarget.querySelector(`[data-item-id="${itemId}"]`)

    if (!item) {
      // Log all data-item-id values in the container for debugging
      const allItems = Array.from(this.containerTarget.querySelectorAll('[data-item-id]'))
      console.log('[ResourceableChannel] Looking for item', {
        searchingFor: itemId,
        found: false,
        allItemIds: allItems.map(el => el.dataset.itemId)
      })
    } else {
      console.log('[ResourceableChannel] Looking for item', { itemId, found: true })
    }

    // Defer the replace if an inline-editable input is currently focused inside
    // the item being replaced. Replacing the outerHTML would detach the input
    // mid-edit, leaving the typed value unsaved. We replay the replace once
    // focus moves out (blur).
    if (item && this.shouldDeferReplace(item)) {
      console.log('[ResourceableChannel] Deferring replace; inline-editable input is focused inside item', { itemId })
      this.deferReplace(item, data)
      return
    }

    if (item) {
      console.log('[ResourceableChannel] Item found, replacing HTML')
      this.showSpinner()
      item.outerHTML = html
      const el = this.containerTarget.querySelector(`[data-item-id="${itemId}"]`)
      console.log('[ResourceableChannel] After replace, element found:', !!el)
      this.handleEffect({ element: el, scroll, close_modal })
      this.showToast('success', message || 'Item updated.')
    } else {
      console.warn('[ResourceableChannel] Item not found with data-item-id:', itemId)
    }
  }

  shouldDeferReplace(item) {
    const active = document.activeElement
    if (!active || active === document.body) return false
    if (!item.contains(active)) return false
    return typeof active.matches === 'function' &&
      active.matches('[data-target~="inline-editable-form.field"]')
  }

  deferReplace(item, data) {
    if (!this.deferredReplaces) this.deferredReplaces = new Map()

    const itemId = data.item_id
    const alreadyWaiting = this.deferredReplaces.has(itemId)
    // Latest data wins — if a second replace lands while the first is still
    // pending, the user should ultimately see the most recent server state.
    this.deferredReplaces.set(itemId, data)

    if (alreadyWaiting) return

    const active = document.activeElement
    const onBlur = () => {
      // setTimeout(0) lets focus settle on the new element before we re-check.
      // If the user tabbed within the same row, focus is now in a sibling cell
      // and we need to defer again rather than replace.
      setTimeout(() => {
        const pending = this.deferredReplaces.get(itemId)
        this.deferredReplaces.delete(itemId)
        if (pending) this.replace(pending)
      }, 0)
    }

    active.addEventListener('blur', onBlur, { once: true })
  }

  remove(data) {
    const { message, item_id: itemId } = data
    console.log('[ResourceableChannel] remove called', { itemId, hasContainer: this.hasContainerTarget })

    if (!this.hasContainerTarget) return

    const item = this.containerTarget.querySelector(`[data-item-id="${itemId}"]`)
    if (item) {
      item.remove()
      document.dispatchEvent(new Event('modal:close'))
      this.showToast('info', message || 'Item removed.')
    }
  }

  removeAll() {
    console.log('[ResourceableChannel] removeAll called', { hasContainer: this.hasContainerTarget })

    if (this.hasContainerTarget) {
      while (this.containerTarget.firstChild) {
        this.containerTarget.removeChild(this.containerTarget.firstChild)
      }
      this.showToast('info', 'All items have been removed.')
    }
  }

  clearContent(data) {
    const { message, item_id: itemId } = data
    console.log('[ResourceableChannel] clearContent called', { itemId, hasContainer: this.hasContainerTarget })

    if (!this.hasContainerTarget) return

    const item = this.containerTarget.querySelector(`[data-item-id="${itemId}"]`)
    if (item) {
      item.innerHTML = ''
      this.showToast('success', message || 'Item cleared.')
    }
  }

  error(data) {
    this.showToast('error', data.message)
    this.hideSpinner()
  }

  handleEffect({ element, scroll = true, close_modal = true } = {}) {
    if (!element) {
      console.log('[ResourceableChannel] handleEffect: no element provided')
      return
    }

    console.log('[ResourceableChannel] handleEffect: adding highlighted class', {
      element: element.tagName,
      itemId: element.dataset.itemId,
      currentClasses: element.className
    })

    if (scroll) element.scrollIntoView({ behavior: 'smooth', block: 'center' })

    element.classList.add('highlighted')
    console.log('[ResourceableChannel] highlighted class added, classes now:', element.className)

    setTimeout(() => {
      element.classList.remove('highlighted')
      console.log('[ResourceableChannel] highlighted class removed after 1s')
    }, 1000)

    this.hideSpinner()
    this.applySticky()
    if (close_modal) document.dispatchEvent(new Event('modal:close'))
  }

  showSpinner() {
    const s = this.spinnerId && document.getElementById(this.spinnerId)
    if (s && s.classList.contains('hidden')) s.classList.remove('hidden')
  }

  hideSpinner() {
    const s = this.spinnerId && document.getElementById(this.spinnerId)
    if (s && !s.classList.contains('hidden')) s.classList.add('hidden')
  }

  handleReceived(data) {
    const { scope, action } = data
    console.log('[ResourceableChannel] Broadcast received', {
      action,
      scope,
      expectedScope: this.subscriptionParams.resource_scope,
      data
    })

    if (!this.isCorrectScope(scope)) {
      console.log('[ResourceableChannel] Scope mismatch, ignoring broadcast')
      return
    }

    if (action && typeof this[action] === 'function') {
      console.log('[ResourceableChannel] Executing action:', action)
      this[action](data)
    } else {
      console.warn('[ResourceableChannel] Unknown action or not a function:', action)
    }
  }

  isCorrectScope(dataScope) {
    const expected = this.subscriptionParams.resource_scope
    return !expected || !dataScope || expected === dataScope
  }

  startPingMonitor() {
    this.pingInterval = setInterval(() => {
      if (this.subscription) this.subscription.send({ action: 'ping' })
    }, parseInt(this.element.dataset.pingInterval || '60000', 10))
  }

  showToast(type, message) {
    switch (type) {
      case 'success': window.toastr.success(message); break
      case 'info': window.toastr.info(message); break
      case 'warning': window.toastr.warning(message); break
      case 'error': window.toastr.error(message); break
      default: console.log(`[Toast] ${type}:`, message)
    }
  }

  applySticky() {
    const scrollContainer = this.element.closest('.overflow-x-auto')
    if (scrollContainer) applyStickyTransforms(scrollContainer)
  }

  getScrollRoot() {
    const root = this.element.querySelector('.overflow-y-auto, .overflow-x-auto')
    if (root) return root
    return this.element.closest('.overflow-y-auto, .overflow-x-auto, [data-scroll-root="true"]') || null
  }

  isPaginationVisibleInRoot(preload = 200) {
    if (!this.hasPaginationTarget) return false
    const root = this.getScrollRoot()
    const r = this.paginationTarget.getBoundingClientRect()
    const rr = root ? root.getBoundingClientRect() : { top: 0, bottom: window.innerHeight }
    return r.top <= rr.bottom + preload
  }
}
